// MATERIAL-UI
import {
  Button,
  Grid,
  Skeleton,
  Stack,
  TableBody,
  TableCell,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";

// COMPONENTS
import LineChart from "src/components/graphs/LineChart";
import Rank from "src/components/markets/Rank";
import WatchToggle from "src/components/markets/WatchToggle";
import CountryOrCryptoFlag from "src/components/common/CountryOrCryptoFlag";
import GainsOrLoses from "src/components/common/GainsOrLoses";

// ASSETS
import formatCoinName from "src/utils/formatCoinName";
import { formatCurrency } from "src/utils/helpers/PriceAndUnitsHelpers";
import { useHistory } from "react-router-dom";
import GA from "src/analytics/googleAnalytics";
import { MarketTableStyles } from "src/components/markets/Table/styles/MarketTable.styles";
import { useIsMobile } from "src/utils/hooks/useIsMobile";
import { useAppDispatch } from "src/store/store";
import { togglePriceAlertModal } from "src/store/features/PriceAlerts.slice";
import PriceAlertModel from "../../PriceAlerts/PriceAlertModel";
import React, { useState } from "react";
import { useMaintenanceState } from "src/store/features/maintenance.slice";
import NotificationAddIcon from "@mui/icons-material/NotificationAdd";
import { trackEvent } from "src/Amplitude";
import { CurrencyConfigRes } from "src/store/features/api/generated";
import MaintananceTooltip from "src/components/maintenance/MaintenanceToolTip";
import { useWatchlist } from "src/hooks/useWatchlist";

interface MarketTableBodyProps {
  isFetching: boolean;
  isSuccess: boolean;
  data: any;
  reduxState: any;
}

interface SelectedCoinType {
  price?: number;
  currencyConfig?: CurrencyConfigRes;
  subtitle?: string;
  logoUrl?: string;
}

const MarketTableBody = (props: MarketTableBodyProps) => {
  const [selected, setSelected] = useState<SelectedCoinType>({});
  const { pricealertcontroller } = useMaintenanceState();
  const { isFetching, data, reduxState } = props;
  const dispatchRedux = useAppDispatch();
  const isMobile = useIsMobile();
  const history = useHistory();
  const classes = MarketTableStyles();
  const theme = useTheme();

  // Use watchlist hook to check if coins are in watchlist for filtering
  const { isCoinInWatchlist } = useWatchlist({
    trackingContext: "Markets",
  });

  // Filter data based on Redux watchlist state when watchlist tab is selected
  // This ensures the table updates immediately when tokens are added/removed
  const filteredData = React.useMemo(() => {
    if (!data?.data) return data;

    // Only filter if we're on the watchlist tab
    if (reduxState?.type === "watchlist") {
      // Filter to only show coins that are in the Redux watchlist
      const filtered = data.data.filter((row: any) => {
        const code = row.currencyConfig?.code;
        if (!code) return false;
        return isCoinInWatchlist(code);
      });
      return { ...data, data: filtered };
    }

    // For other tabs, return data as-is
    return data;
  }, [data, reduxState?.type, isCoinInWatchlist]);
  // NAVIGATE TO COIN PAGE
  const handleCoinPage = (code: string) => {
    GA.coinSelected(window.location.pathname, code);
    history.push(`/coin/${code.toLowerCase()}`);
  };

  const handlePriceAlert = (configCode: string) => {
    dispatchRedux(togglePriceAlertModal());
    trackEvent(`Visit_Modal`, { modal: "Price_Alert_Model", configCode }, "Markets");
    //resets RTKQ state
  };

  const handlePriceAlertData = (row: any) => {
    setSelected({
      price: row.price,
      currencyConfig: row.currencyConfig,
      subtitle: row.currencyConfig.formatName,
      logoUrl: row.currencyConfig.logoUrl,
    });
  };

  return (
    <>
      <PriceAlertModel
        shouldRedirect={false}
        price={selected.price}
        currencyConfig={selected.currencyConfig}
        subtitle={selected.subtitle}
        logoUrl={selected.logoUrl}
      />
      <TableBody sx={{ position: "relative" }}>
        {filteredData?.data.map((row: any) => {
          return (
            <TableRow
              hover
              role="checkbox"
              tabIndex={-1}
              sx={{ height: "86px" }}
              key={row.currencyConfig?.name}
              onClick={() => {
                handleCoinPage(row.currencyConfig?.code);
              }}>
              {/* watch */}
              <TableCell
                style={{
                  padding: "5px",
                  maxWidth: "10px",
                  backgroundColor: isMobile ? "#FFF" : "transparent",
                }}
                onClick={(e) => e.stopPropagation()}>
                <Stack direction="row">
                  <WatchToggle
                    height={"18px"}
                    width={"18px"}
                    code={row.currencyConfig?.code}
                    variant="icon"
                    currencyConfig={row.currencyConfig}
                  />
                </Stack>
              </TableCell>
              {/* rank */}
              <TableCell
                style={{
                  padding: "5px",
                  backgroundColor: isMobile ? "#FFF" : "transparent",
                }}>
                <Rank rank={row.rank} isActive={row.status} />
              </TableCell>
              {/* logo */}
              <TableCell
                style={{
                  position: "sticky",
                  left: "0px",
                  padding: "5px",
                  backgroundColor: isMobile ? "#FFF" : "transparent",
                }}>
                <Grid container alignItems="center" justifyContent="center">
                  <CountryOrCryptoFlag
                    name={row.currencyConfig?.logoUrl || row.currencyConfig?.code}
                    size={30}
                  />
                </Grid>
              </TableCell>
              {/* token name */}
              <TableCell
                style={{
                  padding: "5px",
                  left: "59px",
                  backgroundColor: isMobile ? "#FFF" : "transparent",
                  position: "sticky",
                }}>
                <Grid container>
                  <Grid item xs={12}>
                    <Typography className={classes.code}>
                      {formatCoinName(row.currencyConfig?.name || "BTC (btc)")}
                    </Typography>
                  </Grid>
                  <Grid item xs>
                    <Typography>{row.currencyConfig?.code}</Typography>
                  </Grid>
                </Grid>
              </TableCell>
              {/* token price */}
              {/* Should always default to USD */}
              <TableCell>
                {/* {row.price} */}
                <Stack direction="row" justifyContent="space-between">
                  <Typography sx={{ fontWeight: "500" }} translate="no">
                    {formatCurrency(row.price, "USD")}
                  </Typography>
                </Stack>
              </TableCell>
              {/* 24h change */}
              <TableCell style={{ padding: "1px" }}>
                {<GainsOrLoses balance={row.change1d} />}
              </TableCell>
              {/* 7d change */}
              <TableCell style={{ padding: "1px" }}>
                <GainsOrLoses balance={row.change7d} />
              </TableCell>
              {/* 30d change */}
              <TableCell style={{ padding: "1px" }}>
                <GainsOrLoses balance={row.change30d} />
              </TableCell>
              {/* market cap */}
              <TableCell style={{ padding: "5px" }}>
                {typeof row.cap === "number" ? (
                  <Typography sx={{ fontWeight: "500" }} translate="no">
                    {formatCurrency(row.cap, "USD", {
                      type: "$",
                    })}
                  </Typography>
                ) : (
                  "?"
                )}
              </TableCell>
              {/* last 7 days */}
              <TableCell style={{ padding: "5px" }}>
                {/* not enough vertices to display */}
                {row.chart?.length > 3 && (
                  <LineChart
                    height={50}
                    width={160}
                    hideTooltip
                    color={row.change1d < 0 ? "#EA0C01" : "#01C0AA"}
                    tableBodyGraph={{
                      y: row.chart,
                    }}
                  />
                )}
              </TableCell>
              <TableCell onClick={(e) => e.stopPropagation()}>
                {row.currencyConfig.secondarySector !== "Stablecoin" && (
                  <MaintananceTooltip
                    message="Price alerts are undergoing maintenance. If you need assistance, please contact support"
                    disableHover={pricealertcontroller}>
                    <Button
                      disableRipple
                      sx={{
                        minWidth: "0px",
                        display: "flex",
                        paddingTop: "4px",
                        "&:hover": {
                          cursor: !pricealertcontroller ? "not-allowed" : "pointer",
                          pointerEvents: "all !important",
                          background: pricealertcontroller ? "inherit" : "transparent",
                        },
                      }}
                      onClick={() => {
                        if (pricealertcontroller) {
                          handlePriceAlert(row.currencyConfig.code);
                          handlePriceAlertData(row);
                        }
                      }}>
                      <NotificationAddIcon
                        className={classes.notificationIcon}
                        sx={{
                          width: "25px",
                          height: "25px",
                          paddingBottom: "2px",
                          color: theme.palette.primary.dark,
                        }}
                      />
                    </Button>
                  </MaintananceTooltip>
                )}
              </TableCell>
            </TableRow>
          );
        })}

        {/* ERROR CASE BACKGROUND */}
        {!isFetching &&
          !data &&
          Array.from(Array(10)).map((el, i) => (
            <TableRow
              key={i}
              sx={{
                background: "#000000",
                opacity: "0.1",
                filter: "blur(3px)",
              }}>
              <TableCell colSpan={10}>
                <Skeleton width="100%" height="100%" animation={false} />
              </TableCell>
            </TableRow>
          ))}
      </TableBody>
    </>
  );
};

export default MarketTableBody;
